const INVALID_FILENAME_CHARS = /[\\/:*?"<>|]/g;

const sanitizePrintFilename = (title: string) =>
  title.replace(INVALID_FILENAME_CHARS, "-").replace(/\s+/g, " ").trim();

const PRINT_FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700&family=Poppins:wght@400;600;700&display=swap";

const collectDocumentStyles = () => {
  const chunks: string[] = [];
  Array.from(document.styleSheets).forEach((sheet) => {
    try {
      const rules = sheet.cssRules;
      if (!rules?.length) return;
      chunks.push(Array.from(rules).map((rule) => rule.cssText).join("\n"));
      return;
    } catch {
      /* A cross-origin file cannot be read. The link tag is copied instead. */
    }
    const node = sheet.ownerNode;
    if (node instanceof HTMLStyleElement && node.textContent?.trim()) {
      chunks.push(node.textContent);
    }
  });
  return chunks.join("\n").replace(/<\/style/gi, "<\\/style");
};

/** Production serves CSS as files. Dev injects it into style tags. */
const collectStylesheetLinks = () =>
  Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
    .map((link) => link.href)
    .filter(Boolean)
    .map(
      (href) =>
        `<link rel="stylesheet" href="${href.replace(/"/g, "&quot;")}" />`
    )
    .join("\n");

const documentHtml = (markup: string, title: string) => {
  const safeTitle = title.replace(/[<>&]/g, "");

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <base href="${document.baseURI.replace(/"/g, "&quot;")}" />
  <title>${safeTitle}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link id="print-fonts" href="${PRINT_FONT_HREF}" rel="stylesheet" />
  ${collectStylesheetLinks()}
  <style>${collectDocumentStyles()}</style>
  <style>
    @page { size: A4; margin: 0; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #000;
      height: auto !important;
      overflow: visible !important;
      font-family: "Poppins", Arial, Helvetica, sans-serif;
    }
    * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .urduNotationFont,
    [lang="ur"] {
      font-family: "Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", "Urdu Typesetting", serif !important;
      direction: rtl;
      unicode-bidi: isolate;
      line-height: 2.2;
      text-transform: none !important;
    }
    .MuiTableContainer-root,
    .MuiPaper-root {
      max-height: none !important;
      height: auto !important;
      overflow: visible !important;
    }
    .discharge-print-root {
      display: block !important;
      height: auto !important;
      overflow: visible !important;
    }
    table.discharge-pages {
      display: table !important;
      width: 100%;
      border-collapse: collapse;
    }
    table.discharge-pages > thead {
      display: table-header-group !important;
    }
    table.discharge-pages > tbody {
      display: table-row-group !important;
    }
    table.discharge-pages > tfoot {
      display: table-footer-group !important;
    }
    table.discharge-pages > thead > tr,
    table.discharge-pages > tbody > tr,
    table.discharge-pages > tfoot > tr {
      display: table-row !important;
    }
    table.discharge-pages > thead > tr > td {
      display: table-cell !important;
      padding: 6mm 8mm 0;
      border: none;
      background: transparent;
      vertical-align: top;
    }
    table.discharge-pages > tbody > tr > td {
      display: table-cell !important;
      padding: 0 8mm;
      border: none;
      background: transparent;
      vertical-align: top;
    }
    table.discharge-pages > tbody > tr.report-section-row,
    .report-section {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    table.discharge-pages > tbody > tr.report-section-flow,
    table.discharge-pages > tbody > tr.report-section-flow .report-section {
      break-inside: auto !important;
      page-break-inside: auto !important;
    }
    /* Death certificate / forced-flow bodies must split or Chrome prints a blank first page */
    table.discharge-pages > tbody > tr.report-section-row:has(.force-print-flow),
    table.discharge-pages > tbody > tr.report-section-row:has(.death-cert-body) {
      break-inside: auto !important;
      page-break-inside: auto !important;
    }
    table.discharge-pages > tfoot > tr > td {
      display: table-cell !important;
      padding: 0 8mm;
      border: none;
      background: transparent;
      vertical-align: bottom;
    }
    .prescription-page {
      position: relative;
      z-index: 100;
      background: #fff;
      box-sizing: border-box;
      min-height: 297mm;
      padding: 6mm 8mm 10mm;
      page-break-after: always;
      break-after: page;
    }
    .prescription-sheet {
      display: flex !important;
      flex-direction: column !important;
      min-height: 275mm;
      background: #fff;
    }
    .prescription-doctor-header {
      width: 100%;
      height: 120px;
      object-fit: fill;
      display: block;
      margin: 0 0 8px;
      background: #fff;
      position: relative;
      z-index: 20;
    }
    .prescription-doctor-footer {
      width: 100%;
      height: 70px;
      object-fit: fill;
      display: block;
      margin-top: auto;
    }
    .discharge-letterhead img {
      width: 100%;
      height: 120px;
      object-fit: fill;
      display: block;
    }
    .discharge-page-footer {
      position: fixed !important;
      left: 8mm !important;
      right: 8mm !important;
      bottom: 5mm !important;
      margin: 0 !important;
      background: #fff !important;
      z-index: 10;
    }
    .discharge-footer-note {
      margin: 0 0 2px;
      text-align: center;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.3;
    }
    .discharge-footer-image {
      width: 100%;
      height: 70px;
      object-fit: fill;
      display: block;
    }
    .discharge-footer-meta {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      margin-top: 2px;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.3;
      white-space: nowrap;
    }
    .discharge-footer-spacer {
      height: 42mm;
    }
    .clinical-print .discharge-footer-note,
    .clinical-print .discharge-footer-meta {
      font-size: 13px;
    }
    .clinical-print .MuiTableCell-root {
      font-size: 11px !important;
    }
    .duration-urdu {
      display: inline-flex !important;
      flex-direction: row !important;
      direction: ltr !important;
      unicode-bidi: isolate;
      align-items: baseline;
      white-space: nowrap;
      gap: 4px;
    }
    .duration-number {
      direction: ltr !important;
      unicode-bidi: isolate;
      font-family: "Poppins", Arial, Helvetica, sans-serif !important;
    }
    .discharge-order-sign {
      display: flex !important;
      justify-content: space-between;
      align-items: flex-start;
      gap: 10px;
      width: 100%;
      margin-top: 8px;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .discharge-order-side {
      flex: 1 1 0 !important;
      min-width: 0;
    }
    .discharge-sign-block {
      flex: 0 0 200px;
      width: 200px;
    }
    .discharge-sign-block img {
      width: 120px !important;
      max-width: 120px !important;
      height: auto !important;
      margin-left: 0 !important;
    }
    .discharge-sign-block .discharge-sign-stamp {
      width: 190px !important;
      max-width: 190px !important;
      height: auto !important;
    }
    .discharge-footer-sign {
      display: flex !important;
      flex-direction: column;
      align-items: flex-end;
      margin: 0 0 4px;
    }
    .discharge-footer-sign img {
      position: static !important;
      top: auto !important;
      right: auto !important;
      width: 120px !important;
      max-width: 120px !important;
      height: auto !important;
      margin: 0 0 4px !important;
    }
    .discharge-footer-sign .discharge-sign-stamp {
      width: 190px !important;
      max-width: 190px !important;
    }
    .discharge-visit-box {
      box-sizing: border-box;
      display: block;
      width: 100% !important;
      max-width: none !important;
      margin-top: 6px;
      padding: 8px 10px;
      border: 1px solid #9e9e9e;
      border-radius: 8px;
      font-size: 11px;
      line-height: 1.45;
      color: #000;
    }
    .discharge-visit-title {
      font-weight: 700;
      margin-right: 4px;
    }
    .discharge-visit-intro {
      margin-bottom: 4px;
    }
    .discharge-visit-row {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
    }
    .discharge-visit-label {
      font-weight: 600;
      margin-right: 4px;
    }
    .discharge-note-value {
      font-size: 10px !important;
      font-weight: 400 !important;
      line-height: 1.4;
    }
  </style>
</head>
<body>${markup}</body>
</html>`;
};

/**
 * Cached images often report complete=true before decode; Chrome then prints blank.
 * Wait for load + decode, and treat naturalWidth===0 as still loading.
 */
const waitForImage = async (image: HTMLImageElement) => {
  if (!image?.src) return;

  const needsLoad = !image.complete || image.naturalWidth === 0;
  if (needsLoad) {
    await new Promise<void>((resolve) => {
      const done = () => resolve();
      image.addEventListener("load", done, { once: true });
      image.addEventListener("error", done, { once: true });
      // Force a re-fetch path when the cache left a 0×0 "complete" image
      if (image.complete && image.naturalWidth === 0) {
        const src = image.getAttribute("src") || image.src;
        image.removeAttribute("src");
        image.src = src;
      }
    });
  }

  try {
    if (typeof image.decode === "function") {
      await image.decode();
    }
  } catch {
    /* Broken/optional images should not block print. */
  }
};

/** Wait for letterhead/signature images on the live report node before cloning. */
export const waitForPrintNodeAssets = async (node: HTMLElement) => {
  const images = Array.from(node.querySelectorAll("img"));
  await Promise.race([
    Promise.all(images.map((img) => waitForImage(img))),
    new Promise<void>((resolve) => window.setTimeout(resolve, 5000)),
  ]);
};

const waitForStylesheet = (link: HTMLLinkElement) =>
  link.sheet
    ? Promise.resolve()
    : new Promise<void>((resolve) => {
        link.addEventListener("load", () => resolve(), { once: true });
        link.addEventListener("error", () => resolve(), { once: true });
      });

const backgroundUrls = (doc: Document) => {
  const urls = new Set<string>();
  doc.querySelectorAll<HTMLElement>("*").forEach((element) => {
    const background = element.style.backgroundImage || "";
    const match = /url\(["']?(.+?)["']?\)/.exec(background);
    if (match?.[1]) urls.add(match[1]);
  });
  return Array.from(urls);
};

const preloadUrl = (src: string) =>
  new Promise<void>((resolve) => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => resolve();
    image.src = src;
    if (image.complete) resolve();
  });

const waitForPrintAssets = async (doc: Document) => {
  const stylesheets = Array.from(
    doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
  ).map(waitForStylesheet);
  const images = Array.from(doc.images).map((img) => waitForImage(img));
  const backgrounds = backgroundUrls(doc).map(preloadUrl);
  const fonts = doc.fonts?.ready ?? Promise.resolve();

  await Promise.race([
    Promise.all([
      fonts.catch(() => undefined),
      ...stylesheets,
      ...images,
      ...backgrounds,
    ]),
    new Promise((resolve) => window.setTimeout(resolve, 5000)),
  ]);

  try {
    await Promise.race([
      Promise.all([
        doc.fonts?.load('16px "Noto Nastaliq Urdu"'),
        doc.fonts?.load('12px Poppins'),
        doc.fonts?.ready,
      ]),
      new Promise((resolve) => window.setTimeout(resolve, 3000)),
    ]);
  } catch {
    /* Print with the faces that have already loaded. */
  }

  // Extra layout pass after decode — matches cold-cache behavior after a hard refresh
  void doc.body?.offsetHeight;
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
};

/** Repeat the letterhead and footer image on every printed page. */
const repeatHeaderAndFooter = (doc: Document) => {
  const root = doc.querySelector(".discharge-print-root");
  const header = root?.querySelector(".discharge-letterhead");
  const footer = root?.querySelector(".discharge-page-footer");
  if (!root || (!header && !footer)) return;

  const prescription = root.querySelector(".prescription-page");
  if (prescription) prescription.remove();

  const table = doc.createElement("table");
  table.className = "discharge-pages";

  const thead = doc.createElement("thead");
  const headRow = doc.createElement("tr");
  const headCell = doc.createElement("td");
  if (header) headCell.appendChild(header);
  headRow.appendChild(headCell);
  thead.appendChild(headRow);

  const tfoot = doc.createElement("tfoot");
  const footRow = doc.createElement("tr");
  const footCell = doc.createElement("td");
  const spacer = doc.createElement("div");
  spacer.className = "discharge-footer-spacer";
  footCell.appendChild(spacer);
  if (footer) doc.body.appendChild(footer);
  footRow.appendChild(footCell);
  tfoot.appendChild(footRow);

  const tbody = doc.createElement("tbody");
  const addSectionRow = (node: Node) => {
    const row = doc.createElement("tr");
    row.className = "report-section-row";
    const cell = doc.createElement("td");
    cell.appendChild(node);
    row.appendChild(cell);
    tbody.appendChild(row);
  };
  const appendSections = (parent: ParentNode) => {
    Array.from(parent.childNodes).forEach((node) => {
      if (!(node instanceof HTMLElement)) {
        if (node.textContent?.trim()) addSectionRow(node);
        return;
      }
      const hasNestedSection = !!node.querySelector(".report-section");
      if (hasNestedSection && !node.classList.contains("prescription-page")) {
        appendSections(node);
        return;
      }
      addSectionRow(node);
    });
  };
  appendSections(root);

  table.appendChild(thead);
  table.appendChild(tbody);
  table.appendChild(tfoot);
  if (prescription) root.appendChild(prescription);
  root.appendChild(table);
};

const reserveFooterSpace = (doc: Document) => {
  const footer = doc.querySelector<HTMLElement>(".discharge-page-footer");
  const spacer = doc.querySelector<HTMLElement>(".discharge-footer-spacer");
  if (!footer || !spacer) return;
  const height = Math.ceil(footer.getBoundingClientRect().height);
  const minimum = Math.round((42 * 96) / 25.4);
  spacer.style.height = `${Math.max(height + 16, minimum)}px`;
};

/** A section taller than one page must split. Keeping it whole leaves a blank first page. */
const allowTallSectionsToSplit = (doc: Document) => {
  const mm = (value: number) => (value * 96) / 25.4;
  const header = doc.querySelector(".discharge-letterhead");
  const spacer = doc.querySelector(".discharge-footer-spacer");
  const headerHeight = header?.getBoundingClientRect().height || mm(40);
  const footerHeight = spacer?.getBoundingClientRect().height || mm(42);
  const available = mm(297) - headerHeight - footerHeight - mm(6);

  doc.querySelectorAll<HTMLElement>("tr.report-section-row").forEach((row) => {
    const cell = row.querySelector("td");
    const text = row.textContent?.replace(/\s+/g, " ").trim() || "";
    const forceFlow =
      !!row.querySelector(".force-print-flow, .death-cert-body") ||
      /DEATH CERTIFICATE/i.test(text);

    const height = Math.max(
      row.getBoundingClientRect().height,
      cell?.scrollHeight || 0,
      cell?.offsetHeight || 0
    );

    // Unmeasured layout (0) with real content also blanks page 1 if kept unbreakable
    if (forceFlow || height > available || (height < 8 && text.length > 120)) {
      row.classList.add("report-section-flow");
    }
  });
};

const PRINT_FRAME_STYLE =
  // Must stay in the viewport with tiny opacity — Chrome skips painting
  // opacity:0 / off-screen iframes and the print preview comes out blank.
  "position:fixed;left:0;top:0;width:210mm;height:297mm;border:0;opacity:0.01;pointer-events:none;z-index:2147483646;background:#fff;";

const frameHasContent = (doc: Document) => {
  const text = doc.body?.innerText?.replace(/\s+/g, " ").trim() || "";
  const hasRoot = !!doc.querySelector(".discharge-print-root");
  return hasRoot && text.length > 40;
};

/**
 * Hidden-frame print used by the discharge report.
 * srcdoc (same-origin) + in-viewport frame + content check before print() —
 * avoids intermittent blank Chrome preview when cache is warm.
 */
export const printDischargeReport = (node: HTMLElement, documentTitle: string) =>
  new Promise<void>((resolve, reject) => {
    document.getElementById("discharge-print-frame")?.remove();

    const printTitle = sanitizePrintFilename(documentTitle);
    const previousTitle = document.title;
    const sourceText = node.innerText?.replace(/\s+/g, " ").trim() || "";
    if (sourceText.length < 40) {
      reject(new Error("Print content is not ready."));
      return;
    }

    const html = documentHtml(node.outerHTML, printTitle);
    const iframe = document.createElement("iframe");
    iframe.id = "discharge-print-frame";
    iframe.setAttribute("style", PRINT_FRAME_STYLE);
    iframe.setAttribute("title", printTitle);
    document.body.appendChild(iframe);

    let removed = false;
    let started = false;

    const cleanup = () => {
      if (removed) return;
      removed = true;
      document.title = previousTitle;
      iframe.remove();
    };

    const triggerPrint = (frameWindow: Window, frameDoc: Document) => {
      try {
        if (!frameHasContent(frameDoc)) {
          cleanup();
          reject(new Error("Print frame rendered without content."));
          return;
        }
        reserveFooterSpace(frameDoc);
        allowTallSectionsToSplit(frameDoc);
        void frameDoc.body?.offsetHeight;
        frameWindow.focus();
        frameWindow.print();
        frameWindow.onafterprint = cleanup;
        window.setTimeout(cleanup, 30000);
        resolve();
      } catch (error) {
        cleanup();
        reject(error);
      }
    };

    const start = async () => {
      if (started) return;
      started = true;

      const frameWindow = iframe.contentWindow;
      const frameDoc = frameWindow?.document;
      if (!frameWindow || !frameDoc) {
        cleanup();
        reject(new Error("Unable to open print preview."));
        return;
      }

      document.title = printTitle;
      frameDoc.title = printTitle;
      repeatHeaderAndFooter(frameDoc);

      try {
        for (let attempt = 0; attempt < 10; attempt += 1) {
          if (frameHasContent(frameDoc)) break;
          await new Promise((r) => window.setTimeout(r, 100));
        }

        await waitForPrintAssets(frameDoc);

        if (!frameHasContent(frameDoc)) {
          cleanup();
          reject(new Error("Print content is not ready."));
          return;
        }

        window.setTimeout(() => triggerPrint(frameWindow, frameDoc), 250);
      } catch (error) {
        cleanup();
        reject(error);
      }
    };

    iframe.onload = () => {
      void start();
    };
    // srcdoc keeps the frame same-origin so CSS/fonts apply like the app page
    iframe.srcdoc = html;
    window.setTimeout(() => {
      void start();
    }, 2000);
  });
